<template>
  <div class="bg-white border-[1px] rounded-lg overflow-hidden shadow-sm">
    <div class="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
      <div class="flex items-center">
        <FileText class="w-4 h-4 inline mr-1" />
        <router-link :to="`/applications/all/${applicationId}`" class="text-lg hover:underline">Application Data</router-link>
      </div>
      <div class="text-sm text-gray-500 whitespace-nowrap">
        Updated {{ formatDate(application?.updated_at) }}
      </div>
    </div>
    <div class="p-4">
      <!-- Application data -->
      <div class="px-2 mb-2 rounded-lg flex justify-between items-center">
        <ReferralRequestSentBadge v-if="(applicationsStore.selectedApplication as any)?.referral_request_email_sent" />
      </div>

      <!-- Application Status Dropdowns -->
      <div v-if="applicationsStore.selectedApplication && applicationId" class=" bg-white rounded-lg">
        <UserApplicationStatuses
          :selected-application="applicationsStore.selectedApplication"
          :application-id="applicationId"
        />
      </div>

      <!-- Resume selector (referral/referrer flow only) -->
      <div
        v-if="
          applicationsStore.selectedApplication &&
          applicationId &&
          (isGuide || isOwner) &&
          applicationsStore.selectedApplication.referrers_exist_for_employer !== false
        "
        class="bg-gray-50 rounded-lg p-4 mt-2"
      >
        <ApplicationResumeSelector
          :application="applicationsStore.selectedApplication"
          :application-id="applicationId"
        />
      </div>

      <!-- Referral management -->
      <div v-if="isGuide" class="bg-gray-50 rounded-lg p-4 mt-2">
        <h2 class="text-md flex items-center mb-2">
          <Mail class="w-4 h-4 mr-2" />
          Referral management
        </h2>
        <ReferrerSelection
          v-if="isGuide && applicationsStore.selectedApplication && applicationId"
          :application="applicationsStore.selectedApplication"
          :application-id="applicationId"
        />
        
        <div class="flex items-center gap-2 justify-end text-sm">
          <button
            class="mt-4 bg-lime-300 border-[1px] border-lime-300 rounded-md text-lime-900 font-light px-2 py-1 hover:bg-lime-400 hover:border-lime-400 hover:text-lime-800 transition-all hover:shadow-md hover:translate-y-[-1px] duration-200 flex items-center"
            @click="emit('preview-email')"
          >
            <Eye class="w-4 h-4 mr-2" />
            Preview referral email
          </button>
          <!-- <button
            class="mt-4 bg-lime-300 border-[1px] border-lime-300 rounded-md text-lime-900 font-light px-2 py-1 hover:bg-lime-400 hover:border-lime-400 hover:text-lime-800 transition-all hover:shadow-md hover:translate-y-[-1px] duration-200 flex items-center"
          >
            <Send class="w-3.5 h-3.5 mr-2" />
            Send referral email
          </button> -->
        </div>
      </div>

      <hr class="w-full my-2" />

      <UserApplicationComments
        v-if="applicationsStore.selectedApplication && applicationId"
        :comments="applicationsStore.selectedApplication?.comments || undefined"
        :application-id="applicationId"
        :disabled="!isGuide && applicationsStore.selectedApplication?.user_details?.id !== userStore?.currentUser?.id"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted, computed } from 'vue'
import { formatDate } from '@/utils/formatDate'
import { useUserStore } from '@/stores/user'
import { useApplicationsStore } from '@/stores/applications'
import { useReferrersStore } from '@/stores/referrers'
import { useAuth } from '@/composables/useAuth'
import { useReferrerManagement } from '@/composables/useReferrerManagement'
import {
  FileText,
  Mail,
  Eye
} from 'lucide-vue-next'
import ReferralRequestSentBadge from '@/components/common/badges/ReferralRequestSentBadge.vue'
import UserApplicationStatuses from '@/components/userApplications/UserApplicationStatuses.vue'
import ReferrerSelection from '@/components/userApplications/ReferrerSelection.vue'
import UserApplicationComments from '@/components/userApplications/UserApplicationComments.vue'
import ApplicationResumeSelector from '@/components/common/jobApplication/ApplicationResumeSelector.vue'

interface Props {
  application: any
  applicationId: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'preview-email': []
}>()


const userStore = useUserStore()
const applicationsStore = useApplicationsStore()
const referrersStore = useReferrersStore()
const { isGuide } = useAuth()

const isOwner = computed(() =>
  applicationsStore.selectedApplication?.user_details?.id === userStore.currentUser?.id
)

const {
  initializeReferrerId,
  setupReferrerWatcher
} = useReferrerManagement()




let referrerWatcherStop: (() => void) | null = null

// Watch for application changes to initialize referrer management
watch(
  () => props.application,
  (newApplication) => {
    if (newApplication) {
      initializeReferrerId(newApplication)
      
      // Setup referrer watcher
      if (referrerWatcherStop) {
        referrerWatcherStop()
      }
      referrerWatcherStop = setupReferrerWatcher(props.applicationId)
    } else {
      initializeReferrerId(null)
      if (referrerWatcherStop) {
        referrerWatcherStop()
        referrerWatcherStop = null
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  try {
    await referrersStore.fetchReferrers()
  } catch (error) {
    console.error('Failed to fetch referrers:', error)
  }
})
</script>